import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { getStandardISODate } from "@core/helpers/date-helper";
import { environment } from "@env/environment";
import { FundWithRelated } from "@models/fund-with-related.model";
import { Nav } from "@models/nav.model";
import { NavPeriod } from "@models/nav-period.model";
import { TradeMoment } from "@models/trade-moment-model";
import { Yieldu } from "@models/yieldu-model";
import { map, Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class TradingService {
  constructor(private http: HttpClient) {}

  getFundList$(): Observable<Array<FundWithRelated>> {
    return this.http
      .get<Array<FundWithRelated>>(`${environment.BASE_URL}fund/`)
      .pipe(
        map((response: Array<FundWithRelated>): Array<FundWithRelated> => {
          return response;
        })
      );
  }

  getLatestTrading$(definitive: boolean): Observable<TradeMoment> {
    let params = new HttpParams({});
    if (definitive) {
      params = params.append("definitive", definitive);
    }
    return this.http
      .get<TradeMoment>(`${environment.BASE_URL}trading/latest`, { params })
      .pipe(
        map((response: TradeMoment): TradeMoment => {
          return response;
        })
      );
  }

  generateOrderBook$(): Observable<unknown> {
    return this.http.post(
      `${environment.BASE_URL}trading/generateOrderBook`,
      {}
    );
  }

  updateTransactions$(): Observable<unknown> {
    return this.http.post(
      `${environment.BASE_URL}trading/updateTransactions`,
      {}
    );
  }

  getTransactionsCount$(): Observable<number> {
    return this.http
      .get<number>(`${environment.BASE_URL}banktransactions/unmatched/count`)
      .pipe(
        map((response: number): number => {
          return response;
        })
      );
  }

  postTrading$(tradingCanceled: boolean | undefined): Observable<unknown> {
    if (tradingCanceled) {
      return this.http.patch(`${environment.BASE_URL}trading`, {});
    } else {
      return this.http.post(`${environment.BASE_URL}trading`, {});
    }
  }

  deleteTrading$(): Observable<unknown> {
    return this.http.delete(`${environment.BASE_URL}trading`);
  }

  getTradingReport$(
    registerReport?: boolean,
    dateStart?: Date,
    dateEnd?: Date
  ): Observable<unknown> {
    let params = new HttpParams({});
    if (dateStart && dateEnd) {
      const from = dateStart.toISOString();
      const until = dateEnd.toISOString();
      params = params.append("from", from);
      params = params.append("untill", until);
    }
    if (registerReport) {
      return this.http.get(
        `${environment.BASE_URL}report/cumulativeparticipantregisterreport`
      );
    } else {
      return this.http.get(`${environment.BASE_URL}report/costreport`, {
        params,
      });
    }
  }

  getNavHistory$(
    selectedIds: Array<string>,
    dateStart: string | null,
    dateEnd: string | null
  ): Observable<Array<NavPeriod>> {
    let params = new HttpParams({});
    if (dateStart && dateEnd) {
      const from = getStandardISODate(dateStart);
      const until = getStandardISODate(dateEnd);

      if (from) params = params.append("startDate", from);
      if (until) params = params.append("endDate", until);
    }
    return this.http
      .post<any>(`${environment.BASE_URL}fund/navperiod`, selectedIds, {
        params,
      })
      .pipe(
        map((response: Array<NavPeriod>): Array<NavPeriod> => {
          return response;
        })
      );
  }

  savePendingNav$(navValues: Array<Nav>): Observable<unknown> {
    return this.http.post(`${environment.BASE_URL}pendingnav/`, { navValues });
  }

  getYielduEfficiency$(): Observable<Array<Yieldu>> {
    return this.http
      .get<Array<Yieldu>>(`${environment.BASE_URL}yieldu/all`)
      .pipe(
        map((response: Array<Yieldu>): Array<Yieldu> => {
          return response;
        })
      );
  }

  saveYielduEfficiency$(yieldu: Array<Yieldu>): Observable<unknown> {
    return this.http.post(`${environment.BASE_URL}yieldu/`, yieldu);
  }

  checkTradeStatus$(): Observable<boolean> {
    return this.http.post<boolean>(
      `${environment.BASE_URL}trading/canTrade/`,
      undefined
    );
  }
}
